import { jsPDF } from 'jspdf';

// ============================================================
// HELPERS COMUNES PARA PDFs ESTANDARIZADOS
// src/services/pdfCommon.js
// ============================================================

export const COLORS_PDF = {
  primary: [26, 58, 92],
  teal: [44, 184, 160],
  text: [30, 30, 30],
  muted: [110, 120, 130],
  light: [244, 246, 248],
  border: [220, 225, 230],
  white: [255, 255, 255],
};

const setText = (doc, rgb) => doc.setTextColor(...rgb);
const setFill = (doc, rgb) => doc.setFillColor(...rgb);
const setDraw = (doc, rgb) => doc.setDrawColor(...rgb);

const DEFAULT_BRANCH = {
  name: 'Hospital Clínico Veterinario UDLA',
  displayName: 'Hospital Veterinario UDLA',
  address: 'Av. República 71, Santiago',
  phone: '+56 2 2222 2222',
  email: '',
  website: '',
};

export const normalizeBranch = (b = {}) => ({
  name: b?.name || b?.nombre || DEFAULT_BRANCH.name,
  displayName: b?.displayName || DEFAULT_BRANCH.displayName,
  address: b?.address || b?.direccion || DEFAULT_BRANCH.address,
  phone: b?.phone || b?.telefono || DEFAULT_BRANCH.phone,
  email: b?.email || DEFAULT_BRANCH.email,
  website: b?.website || b?.web || DEFAULT_BRANCH.website,
});

/**
 * Carga una imagen desde una URL y la devuelve como dataURL base64.
 * Retorna null si falla (por ejemplo, por CORS o ruta rota).
 */
const loadImageAsBase64 = (src) =>
  new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = () => resolve(null);
    img.src = src;
  });

/**
 * Precarga los logos que se usarán en los PDFs.
 * Idealmente se llama una sola vez al inicio del generador async.
 */
export const loadLogos = async () => {
  const [udla, hospital] = await Promise.all([
    loadImageAsBase64('/logo-udla.png'),
    loadImageAsBase64('/HOSPITAL-VETERINARIO.png'),
  ]);
  return { udla, hospital };
};

/**
 * Dibuja el header estándar en la página actual del jsPDF.
 *
 * @param {jsPDF} doc
 * @param {object} branch - Sucursal normalizada
 * @param {object} logos  - { udla: base64, hospital: base64 }
 * @param {object} meta   - { title, number, date }
 * @param {number} headerHeight - alto del header en mm (default 30)
 */
export const renderStandardHeader = (doc, branch, logos, meta, headerHeight = 33) => {
  const pw = doc.internal.pageSize.getWidth();
  const margin = 16;
  const yLogo = 6;
  const logoH = 11;

  // Fondo sutilmente blanco; dejamos el header sin color de fondo para un diseño limpio
  // Logos izquierda
  let x = margin;
  if (logos?.udla) {
    const ar = 400 / 100; // aproximación de aspecto, jsPDF ajusta con ancho/alto
    const w = logoH * 3.2;
    doc.addImage(logos.udla, 'PNG', x, yLogo, w, logoH, undefined, 'FAST');
    x += w + 3;
  }
  if (logos?.hospital) {
    const w = logoH * 3.2;
    doc.addImage(logos.hospital, 'PNG', x, yLogo, w, logoH, undefined, 'FAST');
  }

  // Nombre del hospital y sucursal
  const textX = x + (logos?.hospital ? logoH * 3.2 + 6 : 0);
  setText(doc, COLORS_PDF.primary);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.text('Hospital Veterinario UDLA', textX, 11);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  setText(doc, COLORS_PDF.muted);
  doc.text(branch.name || 'Sucursal', textX, 16);

  // Tipo de documento, número y fecha (derecha)
  const rightX = pw - margin;
  setText(doc, COLORS_PDF.primary);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  if (meta.title) {
    doc.text(meta.title, rightX, 19, { align: 'right' });
  }
  if (meta.number) {
    doc.setFontSize(10);
    doc.text(`N° ${meta.number}`, rightX, 25, { align: 'right' });
  }
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  setText(doc, COLORS_PDF.muted);
  doc.text(meta.date || new Date().toLocaleDateString('es-CL'), rightX, 30, { align: 'right' });

  // Línea divisoria sutil
  setDraw(doc, COLORS_PDF.border);
  doc.setLineWidth(0.4);
  doc.line(margin, headerHeight - 2, pw - margin, headerHeight - 2);
};

/**
 * Dibuja el footer estándar en todas las páginas del documento.
 *
 * @param {jsPDF} doc
 * @param {object} branch - Sucursal normalizada
 * @param {number} footerY - posición Y superior del footer (default 287)
 */
export const renderStandardFooter = (doc, branch, footerY = 287) => {
  const pw = doc.internal.pageSize.getWidth();
  const margin = 16;
  const totalPages = doc.getNumberOfPages();

  const contactTop = [branch.address, branch.phone].filter(Boolean).join('  ·  ');
  const contactBottom = [branch.email, branch.website].filter(Boolean).join('  ·  ');

  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    setDraw(doc, COLORS_PDF.border);
    doc.setLineWidth(0.3);
    doc.line(margin, footerY, pw - margin, footerY);

    setText(doc, COLORS_PDF.muted);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);

    // Número de página a la derecha, arriba del bloque de contacto
    doc.text(`Página ${i} de ${totalPages}`, pw - margin, footerY + 3.5, { align: 'right' });

    if (contactTop) {
      doc.text(contactTop, margin, footerY + 3.5, { maxWidth: 135 });
    }
    if (contactBottom) {
      doc.text(contactBottom, pw / 2, footerY + 7.5, { align: 'center' });
    }
  }
};
